import React from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Customized,
} from "recharts";

// ---- Demo data (replace with your real series) ----
const demoData = [
  { date: "2022-09-01", n1: 2.0, n2: 5.0 },
  { date: "2023-09-01", n1: 4.8, n2: 5.1 },
  { date: "2024-09-01", n1: 6.6, n2: 5.3 },
  { date: "2025-09-01", n1: 9.2, n2: 5.5 },
];

// Legend config (color dots + labels)
const defaultSeries = [
  { key: "n1", label: "Nodule  Number 1", color: "#9747FF" }, // purple-500
  { key: "n2", label: "Nodule  Number 2", color: "#00A3FF" }, // cyan-400
];

// Formatters
const formatDateTick = (iso) => {
  try {
    const d = new Date(iso);
    return new Intl.DateTimeFormat("en", {
      month: "short",
      year: "numeric",
    }).format(d);
  } catch {
    return iso;
  }
};
const formatMm = (v) => `${v} mm`;

// Minimal custom tooltip
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md bg-[#1f1f1f] text-white text-xs shadow px-3 py-2 border border-[#333]">
      <div className="opacity-80 mb-1">{formatDateTick(label)}</div>
      {payload.map((p) => (
        <div key={p.dataKey} className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: p.stroke }}
          />
          <span>{p.name}</span>
          <span className="opacity-80 ml-1">{formatMm(p.value)}</span>
        </div>
      ))}
    </div>
  );
};

// Animated dot that scales in on mount
function AnimatedDot({ cx, cy, r = 7, color, fill, background = "#212121" }) {
  const [scale, setScale] = React.useState(0);
  React.useEffect(() => {
    const id = requestAnimationFrame(() => setScale(1));
    return () => cancelAnimationFrame(id);
  }, []);
  const dotColor = color || fill;
  return (
    <g
      transform={`translate(${cx}, ${cy}) scale(${scale})`}
      style={{ transition: "transform 420ms cubic-bezier(0.22,1,0.36,1)" }}>
      <circle
        cx={0}
        cy={0}
        r={r}
        fill={dotColor}
        stroke={background}
        strokeWidth={5}
      />
    </g>
  );
}

export default function Graph({
  onClose,
  data = demoData,
  series = defaultSeries,
  title = "Finding",
  subtitle, // e.g. "2 Nodules were detected"
}) {
  const [isChartReady, setIsChartReady] = React.useState(false);
  const [showLines, setShowLines] = React.useState(false);

  React.useEffect(() => {
    // Small delay to ensure component is mounted
    const readyTimer = setTimeout(() => {
      setIsChartReady(true);
    }, 50);

    // Delay showing lines to prevent flash
    const linesTimer = setTimeout(() => {
      setShowLines(true);
    }, 100);

    return () => {
      clearTimeout(readyTimer);
      clearTimeout(linesTimer);
    };
  }, []);

  const nodulesCount = series.length;
  const subtitleText =
    subtitle ??
    `${nodulesCount} Nodule${nodulesCount > 1 ? "s" : ""} were detected`;

  // nice y ticks like 1,3,5,...,15
  const yTicks = [1, 3, 5, 7, 9, 11, 13, 15];

  return (
    <div className="fixed inset-0 z-[9999] bg-black/60 flex items-center justify-center p-2">
      <div
        className="bg-[#212121] text-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
        style={{
          width: "min(95vw, 1400px)",
          aspectRatio: "1443 / 698",
        }}>
        {/* Header */}
        <div className="px-3 p-8 border-b border-[#3A3A3A]">
          <div className="flex items-start gap-2">
            <div className="flex-1">
              <div className="text-[16px] font-semibold leading-none">
                {title}
              </div>

              {/* subtitle + legend inline */}
              <div className="mt-1 flex items-center gap-10">
                <div className="text-[14px] opacity-80">{subtitleText}</div>

                <div className="flex items-center gap-8">
                  {series.map((s) => (
                    <div
                      key={s.key}
                      className="flex items-center gap-3 text-[14px]">
                      <span
                        className="inline-block w-4 h-4 rounded-full"
                        style={{ background: s.color }}
                      />
                      <span className="whitespace-nowrap">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Close */}
            <button
              onClick={onClose}
              className="ml-2 text-white/80 text-[25px] hover:text-white text-sm px-1 leading-none"
              aria-label="Close">
              ×
            </button>
          </div>
        </div>

        {/* Chart */}
        <div className="flex-1 p-10">
          <div className="w-full h-full bg-[#212121] rounded-md shadow-inner">
            {isChartReady ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                  margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                  {/* Vertical (date) grid only */}

                  <CartesianGrid
                    stroke="#3A3A3A"
                    strokeOpacity={0.35}
                    vertical={true}
                    horizontal={false}
                  />

                  <XAxis
                    dataKey="date"
                    tickFormatter={formatDateTick}
                    stroke="#9CA3AF"
                    tick={{ fontSize: 14 }}
                    axisLine={true}
                    tickLine={{ stroke: "#3A3A3A" }}
                    tickMargin={18}
                    interval="preserveStartEnd"
                    minTickGap={16}
                  />

                  <YAxis
                    ticks={[1, 3, 5, 7, 9, 11, 13, 15]}
                    domain={[1, 15]}
                    tickFormatter={formatMm}
                    stroke="#9CA3AF"
                    tick={{ fontSize: 14 }}
                    axisLine={{ stroke: "#3A3A3A" }}
                    tickLine={{ stroke: "#3A3A3A" }}
                  />
                  <Tooltip content={<ChartTooltip />} />

                  {showLines && series.map((s) => (
                    <Line
                      key={s.key}
                      type="monotone"
                      name={s.label}
                      dataKey={s.key}
                      stroke={s.color}
                      strokeWidth={2}
                      // Line animation
                      isAnimationActive={true}
                      animationBegin={0}
                      animationDuration={900}
                      animationEasing="ease-out"
                      // Dots animate via custom component
                      dot={<AnimatedDot color={s.color} r={7} />}
                      activeDot={{
                        r: 7,
                        fill: s.color,
                        stroke: "#212121",
                        strokeWidth: 2,
                      }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-gray-400">Loading chart...</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
